import type { GrupoProduto } from '../types/database'
import { GRUPOS } from '../types/database'
import type { DirecaoArredondamentoEmbalagem, ProdutoConversao } from './produtoConversaoKg'
import {
  arredondarKgParaEmbalagensFechadas,
  kgTemFracaoEmbalagem,
  opcoesArredondamentoEmbalagem,
  valorTotalPorKgPedido,
} from './produtoConversaoKg'
import type { PrecoRegistro } from './precoVigente'
import { resolvePrecoVigentePorProduto } from './precoVigente'
import { mensagemErroNecessidadePedido } from './authErrors'
import { supabase } from './supabase'

/** @deprecated use JUSTIFICATIVA_ARREDONDAMENTO_MAIS */
export const JUSTIFICATIVA_ARREDONDAMENTO_EMBALAGEM = 'Arredondamento para embalagem fechada'
export const JUSTIFICATIVA_ARREDONDAMENTO_MAIS = 'Arredondamento para mais (embalagem fechada)'
export const JUSTIFICATIVA_ARREDONDAMENTO_MENOS = 'Arredondamento para menos (embalagem fechada)'

export function justificativaArredondamentoEmbalagem(
  direcao: DirecaoArredondamentoEmbalagem,
): string {
  return direcao === 'menos' ? JUSTIFICATIVA_ARREDONDAMENTO_MENOS : JUSTIFICATIVA_ARREDONDAMENTO_MAIS
}

export function isJustificativaArredondamentoEmbalagem(justificativa?: string | null): boolean {
  const j = justificativa?.trim() || ''
  return (
    j === JUSTIFICATIVA_ARREDONDAMENTO_EMBALAGEM ||
    j === JUSTIFICATIVA_ARREDONDAMENTO_MAIS ||
    j === JUSTIFICATIVA_ARREDONDAMENTO_MENOS
  )
}

/** Chave de seleção no modal: grupos comerciais + alimentos sem produto. */
export type GrupoPedidoSelecao = GrupoProduto | 'sem_grupo'

export const GRUPOS_PEDIDO_SELECAO: { value: GrupoPedidoSelecao; label: string }[] = [
  ...GRUPOS,
  { value: 'sem_grupo', label: 'Sem grupo' },
]

export function todosGruposPedidoSelecao(): Set<GrupoPedidoSelecao> {
  return new Set(GRUPOS_PEDIDO_SELECAO.map((g) => g.value))
}

/**
 * Resolve o grupo comercial do alimento via produto vinculado.
 * Sem produto → 'sem_grupo'.
 */
export function grupoPedidoDoAlimento(
  alimentoId: string,
  grupoPorAlimentoId: Map<string, GrupoProduto | null | undefined>,
): GrupoPedidoSelecao {
  const g = grupoPorAlimentoId.get(alimentoId)
  if (g == null || g === undefined) return 'sem_grupo'
  return g
}

export function filtrarNecessidadePorGrupos(
  rows: NecessidadePedidoRow[],
  selecionados: Set<GrupoPedidoSelecao>,
  grupoPorAlimentoId: Map<string, GrupoProduto | null | undefined>,
): NecessidadePedidoRow[] {
  if (!selecionados.size) return []
  return rows.filter((row) =>
    selecionados.has(grupoPedidoDoAlimento(row.alimento_id, grupoPorAlimentoId)),
  )
}

/** União dos grupos marcados (exceto sem_grupo) com os grupos dos itens da grade. */
export function resolverGruposProdutoParaSalvar(
  selecionados: Set<GrupoPedidoSelecao>,
  itensAlimentoIds: string[],
  grupoPorAlimentoId: Map<string, GrupoProduto | null | undefined>,
): GrupoProduto[] {
  const out = new Set<GrupoProduto>()
  for (const g of selecionados) {
    if (g !== 'sem_grupo') out.add(g)
  }
  for (const id of itensAlimentoIds) {
    if (!id) continue
    const g = grupoPorAlimentoId.get(id)
    if (g) out.add(g)
  }
  const order = GRUPOS.map((x) => x.value)
  return order.filter((g) => out.has(g))
}

export function formatGruposProdutoLabels(grupos: GrupoProduto[] | null | undefined): string {
  if (!grupos?.length) return '—'
  const labels = new Map(GRUPOS.map((g) => [g.value, g.label]))
  return grupos.map((g) => labels.get(g) || g).join(' · ')
}

export type NecessidadePedidoRow = {
  alimento_id: string
  alimento: string
  grupo: string
  unidade: string
  quantidade_pnae: number
  quantidade_cardapio: number
}

export type PedidoItemDraft = {
  key: string
  alimento_id: string
  alimento_nome: string
  alimento_grupo: string
  unidade: string
  quantidade_pnae: string
  quantidade: string
  valor_unitario: string
  justificativa: string
  /** Escolha de pacote fechado quando a necessidade é fracionária. */
  embalagem_direcao?: DirecaoArredondamentoEmbalagem | ''
}

export function mesReferencia(ano: number, mes: number): string {
  return `${ano}-${String(mes).padStart(2, '0')}-01`
}

export function parseMesReferencia(mesRef: string): { ano: number; mes: number } {
  const [ano, mes] = mesRef.split('-').map(Number)
  return { ano, mes }
}

export function ultimoDiaMesRef(mesRef: string): string {
  const { ano, mes } = parseMesReferencia(mesRef)
  const d = new Date(ano, mes, 0)
  return `${ano}-${String(mes).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function formatMesReferencia(mesRef: string): string {
  const { ano, mes } = parseMesReferencia(mesRef)
  return `${String(mes).padStart(2, '0')}/${ano}`
}

export function isoDateOnly(iso: string): string {
  return iso.slice(0, 10)
}

/** A1: competência = primeiro dia do mês da data inicial do período. */
export function competenciaFromPeriodoInicio(inicio: string): string {
  const d = isoDateOnly(inicio)
  const [y, m] = d.split('-')
  return `${y}-${m}-01`
}

export type ValidacaoPeriodoResult = { ok: true } | { ok: false; error: string }

/** B1: início e fim no mesmo mês civil. */
export function validarPeriodoMesmoMes(inicio: string, fim: string): ValidacaoPeriodoResult {
  if (!inicio || !fim) {
    return { ok: false, error: 'Informe a data inicial e a data final do período de consumo.' }
  }
  if (inicio > fim) {
    return { ok: false, error: 'A data inicial deve ser anterior ou igual à data final.' }
  }
  const mesIni = inicio.slice(0, 7)
  const mesFim = fim.slice(0, 7)
  if (mesIni !== mesFim) {
    return {
      ok: false,
      error: 'O período deve começar e terminar no mesmo mês civil.',
    }
  }
  return { ok: true }
}

export function periodoMesInteiro(mesRef: string): { inicio: string; fim: string } {
  return { inicio: mesRef.slice(0, 10), fim: ultimoDiaMesRef(mesRef) }
}

export function periodoPrimeiraQuinzena(mesRef: string): { inicio: string; fim: string } {
  const { ano, mes } = parseMesReferencia(mesRef)
  const mm = String(mes).padStart(2, '0')
  return { inicio: `${ano}-${mm}-01`, fim: `${ano}-${mm}-15` }
}

export function periodoSegundaQuinzena(mesRef: string): { inicio: string; fim: string } {
  const { ano, mes } = parseMesReferencia(mesRef)
  const mm = String(mes).padStart(2, '0')
  return { inicio: `${ano}-${mm}-16`, fim: ultimoDiaMesRef(mesRef) }
}

/** Default sugerido para pedido semanal (7 dias, limitado ao mês civil — B1). */
export function periodoSemanaSugerido(mesRef: string): { inicio: string; fim: string } {
  const inicio = mesRef.slice(0, 10)
  const { ano, mes } = parseMesReferencia(mesRef)
  const ultimo = ultimoDiaMesRef(mesRef)
  const d = new Date(ano, mes - 1, Number(inicio.slice(8, 10)))
  d.setDate(d.getDate() + 6)
  const fimCalc = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return { inicio, fim: fimCalc > ultimo ? ultimo : fimCalc }
}

export function formatPeriodoConsumo(inicio: string, fim: string): string {
  const fmt = (iso: string) => {
    const [y, m, d] = iso.slice(0, 10).split('-')
    return `${d}/${m}/${y}`
  }
  return `${fmt(inicio)} – ${fmt(fim)}`
}

export async function fetchNecessidadePeriodoPedido(inicio: string, fim: string) {
  const { data, error } = await supabase.rpc('necessidade_pedido_periodo', {
    p_inicio: inicio,
    p_fim: fim,
    p_grupo: null,
  })
  if (error) throw new Error(mensagemErroNecessidadePedido(error.message))
  return normalizeNecessidadeRows(data || [])
}

/** C2 no cliente (reforço; o banco valida em assert_compra_periodo_valido). */
export function periodosSobrepoem(aIni: string, aFim: string, bIni: string, bFim: string): boolean {
  return aIni <= bFim && aFim >= bIni
}

/** Null/vazio = todos os grupos comerciais (legado). */
export function gruposEfetivos(grupos: GrupoProduto[] | null | undefined): GrupoProduto[] {
  if (!grupos?.length) return GRUPOS.map((g) => g.value)
  return [...new Set(grupos)]
}

export function gruposIntersectam(
  a: GrupoProduto[] | null | undefined,
  b: GrupoProduto[] | null | undefined,
): GrupoProduto[] {
  const setB = new Set(gruposEfetivos(b))
  return gruposEfetivos(a).filter((g) => setB.has(g))
}

export type PedidoCardapioSobreposicao = {
  id: string
  codigo: string
  gruposConflito: GrupoProduto[]
}

export function findPedidoCardapioSobreposto(
  pedidos: {
    id: string
    codigo: string
    avulso: boolean
    periodo_inicio: string
    periodo_fim: string
    mes_referencia: string | null
    grupos_produto?: GrupoProduto[] | null
  }[],
  inicio: string,
  fim: string,
  grupos: GrupoProduto[] | null | undefined,
  excluirId?: string,
): PedidoCardapioSobreposicao | null {
  const mes = competenciaFromPeriodoInicio(inicio)
  for (const p of pedidos) {
    if (p.avulso) continue
    if (excluirId && p.id === excluirId) continue
    if ((p.mes_referencia || '').slice(0, 10) !== mes) continue
    if (!periodosSobrepoem(inicio, fim, p.periodo_inicio.slice(0, 10), p.periodo_fim.slice(0, 10))) {
      continue
    }
    const conflito = gruposIntersectam(grupos, p.grupos_produto)
    if (conflito.length) {
      return { id: p.id, codigo: p.codigo, gruposConflito: conflito }
    }
  }
  return null
}

export type ExcessoPedidoMensalItem = {
  alimento_id: string
  nome: string
  previsto: number
  pedido: number
  excesso: number
}

export type ExcessoPedidoMensalResult = {
  previstoKg: number
  pedidoKg: number
  excessoKg: number
  alimentosExcedidos: ExcessoPedidoMensalItem[]
}

/** Compara necessidade mensal vs soma já pedida + rascunho (aviso; não bloqueia). */
export function avaliarExcessoPedidoMensal(input: {
  necessidadeMes: { alimento_id: string; alimento: string; quantidade_pnae: number }[]
  pedidosItens: { alimento_id: string; quantidade: number }[]
  rascunhoItens: { alimento_id: string; quantidade: number }[]
}): ExcessoPedidoMensalResult {
  const previstoMap = new Map<string, { nome: string; previsto: number }>()
  for (const n of input.necessidadeMes) {
    const prev = previstoMap.get(n.alimento_id)
    const q = Number(n.quantidade_pnae) || 0
    if (prev) prev.previsto += q
    else previstoMap.set(n.alimento_id, { nome: n.alimento, previsto: q })
  }

  const pedidoMap = new Map<string, number>()
  for (const it of [...input.pedidosItens, ...input.rascunhoItens]) {
    if (!it.alimento_id) continue
    const q = Number(it.quantidade) || 0
    if (q <= 0) continue
    pedidoMap.set(it.alimento_id, (pedidoMap.get(it.alimento_id) || 0) + q)
  }

  let previstoKg = 0
  let pedidoKg = 0
  const alimentosExcedidos: ExcessoPedidoMensalItem[] = []

  const ids = new Set([...previstoMap.keys(), ...pedidoMap.keys()])
  for (const id of ids) {
    const meta = previstoMap.get(id)
    const previsto = meta?.previsto ?? 0
    const pedido = pedidoMap.get(id) ?? 0
    previstoKg += previsto
    pedidoKg += pedido
    if (pedido > previsto + 0.0005) {
      alimentosExcedidos.push({
        alimento_id: id,
        nome: meta?.nome || id,
        previsto,
        pedido,
        excesso: pedido - previsto,
      })
    }
  }

  alimentosExcedidos.sort((a, b) => b.excesso - a.excesso)
  return {
    previstoKg,
    pedidoKg,
    excessoKg: Math.max(0, pedidoKg - previstoKg),
    alimentosExcedidos,
  }
}

export function formatAvisoExcessoPedidoMensal(r: ExcessoPedidoMensalResult): string | null {
  if (r.excessoKg <= 0.0005 && !r.alimentosExcedidos.length) return null
  const nomes = r.alimentosExcedidos
    .slice(0, 8)
    .map((a) => a.nome)
    .join(', ')
  const mais =
    r.alimentosExcedidos.length > 8 ? ` (+${r.alimentosExcedidos.length - 8})` : ''
  const kg = r.excessoKg.toLocaleString('pt-BR', { maximumFractionDigits: 2 })
  const n = r.alimentosExcedidos.length
  if (n === 0) {
    return `Volume mensal excedido em ${kg} kg.`
  }
  if (r.excessoKg <= 0.0005) {
    return `${n} alimento(s) acima da previsão mensal: ${nomes}${mais}.`
  }
  return `Volume mensal excedido em ${kg} kg. ${n} alimento(s) acima da previsão: ${nomes}${mais}.`
}

/** Quantidade ainda disponível no pedido (pedida − alocada). */
export function quantidadeRestante(item: {
  quantidade: number | string
  quantidade_alocada?: number | string | null
}): number {
  const pedida = Number(item.quantidade) || 0
  const alocada = Number(item.quantidade_alocada) || 0
  return Math.max(0, pedida - alocada)
}

export function pedidoTemAlocacao(
  itens: { quantidade_alocada?: number | string | null }[] | undefined,
): boolean {
  return (itens || []).some((i) => (Number(i.quantidade_alocada) || 0) > 0)
}

export function valorTotalItemPedido(
  item: { quantidade: number | string; valor_unitario: number | string },
  produto?: ProdutoConversao | null,
): number {
  const qtdKg = Number(item.quantidade) || 0
  const valorUnit = Number(item.valor_unitario) || 0
  if (produto) {
    const proporcional = valorTotalPorKgPedido(qtdKg, valorUnit, produto)
    if (proporcional != null) return proporcional
  }
  return qtdKg * valorUnit
}

export function valorTotalItens(
  itens: { quantidade: number | string; valor_unitario: number | string }[],
  produtoPorAlimentoId?: Map<string, ProdutoConversao | null>,
  alimentoIdDe?: (item: { alimento_id?: string }) => string | undefined,
): number {
  return itens.reduce((s, i) => {
    const alimentoId = alimentoIdDe?.(i as { alimento_id?: string })
    const produto = alimentoId ? produtoPorAlimentoId?.get(alimentoId) : null
    return s + valorTotalItemPedido(i, produto)
  }, 0)
}

export function buildItensFromNecessidade(
  rows: NecessidadePedidoRow[],
  precos: PrecoRegistro[],
  refDate: string,
  alimentoProdutoIds?: Map<string, string | null>,
  produtoPorAlimentoId?: Map<string, ProdutoConversao | null>,
) {
  // Pedido grava a necessidade do cardápio sem arredondar; embalagem fechada
  // é resolvida no Planejamento de rota (por escola), depois validação ISEN.
  return rows.map((row) => {
    const qtdPnae = Number(row.quantidade_pnae) || 0
    const qtdCardapio = Number(row.quantidade_cardapio) || 0
    void produtoPorAlimentoId
    const preco = resolvePrecoVigentePorProduto(
      precos,
      alimentoProdutoIds?.get(row.alimento_id) ?? null,
      refDate,
    )
    const draft: PedidoItemDraft = {
      key: crypto.randomUUID(),
      alimento_id: row.alimento_id,
      alimento_nome: row.alimento,
      alimento_grupo: row.grupo,
      unidade: row.unidade || 'kg',
      quantidade_pnae: String(qtdPnae),
      quantidade: String(qtdCardapio),
      valor_unitario: preco ? String(preco.valor_unitario) : '',
      justificativa: '',
      embalagem_direcao: '',
    }
    return draft
  })
}

/** True quando a pedida diverge da referência por pacote fechado (mais ou menos). */
export function itemArredondadoPorEmbalagem(
  quantidadeReferenciaKg: number,
  quantidadePedidaKg: number,
  produto?: ProdutoConversao | null,
  justificativa?: string | null,
): boolean {
  if (isJustificativaArredondamentoEmbalagem(justificativa)) return true
  if (!produto) return false
  if (!Number.isFinite(quantidadeReferenciaKg) || !Number.isFinite(quantidadePedidaKg)) return false
  if (Math.abs(quantidadeReferenciaKg - quantidadePedidaKg) <= 0.0005) return false
  const opcoes = opcoesArredondamentoEmbalagem(quantidadeReferenciaKg, produto)
  if (!opcoes?.precisaEscolha) return false
  return (
    Math.abs(quantidadePedidaKg - opcoes.mais.kg) <= 0.0005 ||
    Math.abs(quantidadePedidaKg - opcoes.menos.kg) <= 0.0005
  )
}

export function direcaoArredondamentoDoItem(
  quantidadeReferenciaKg: number,
  quantidadePedidaKg: number,
  produto?: ProdutoConversao | null,
  justificativa?: string | null,
  embalagemDirecao?: DirecaoArredondamentoEmbalagem | '' | null,
): DirecaoArredondamentoEmbalagem | null {
  if (embalagemDirecao === 'mais' || embalagemDirecao === 'menos') return embalagemDirecao
  if (justificativa?.trim() === JUSTIFICATIVA_ARREDONDAMENTO_MENOS) return 'menos'
  if (
    justificativa?.trim() === JUSTIFICATIVA_ARREDONDAMENTO_MAIS ||
    justificativa?.trim() === JUSTIFICATIVA_ARREDONDAMENTO_EMBALAGEM
  ) {
    return 'mais'
  }
  const opcoes = opcoesArredondamentoEmbalagem(quantidadeReferenciaKg, produto)
  if (!opcoes?.precisaEscolha) return null
  if (Math.abs(quantidadePedidaKg - opcoes.mais.kg) <= 0.0005) return 'mais'
  if (Math.abs(quantidadePedidaKg - opcoes.menos.kg) <= 0.0005) return 'menos'
  return null
}

export function formatAvisoArredondamentoEmbalagem(
  itens: { nome: string; referenciaKg: number; pedidaKg: number; direcao?: DirecaoArredondamentoEmbalagem | null }[],
): string | null {
  if (!itens.length) return null
  const n = itens.length
  const linhas = itens
    .slice(0, 8)
    .map((i) => {
      const de = i.referenciaKg.toLocaleString('pt-BR', { maximumFractionDigits: 3 })
      const para = i.pedidaKg.toLocaleString('pt-BR', { maximumFractionDigits: 3 })
      const seta = i.direcao === 'menos' ? '↓' : i.direcao === 'mais' ? '↑' : '→'
      return `${i.nome} (${de} ${seta} ${para} kg)`
    })
    .join('; ')
  const mais = itens.length > 8 ? ` (+${itens.length - 8})` : ''
  return `${n} item(ns) precisam de pacote fechado — escolha para mais ou para menos em cada linha: ${linhas}${mais}.`
}

/**
 * Aplica arredondamento de embalagem na qtd pedida.
 * - Com `direcao` explícita (clique Mais/Menos): aplica a partir do PNAE.
 * - Com `embalagem_direcao` no draft: reaplica a escolha.
 * - Sem direção: só corrige se a quantidade atual ainda estiver fracionária (default: mais).
 */
export function aplicarArredondamentoEmbalagemDraft(
  draft: PedidoItemDraft,
  produto?: ProdutoConversao | null,
  direcao?: DirecaoArredondamentoEmbalagem,
): PedidoItemDraft {
  if (!produto) return draft
  const refKg = Number(draft.quantidade_pnae)
  const qtdAtual = Number(draft.quantidade)
  const baseKg =
    Number.isFinite(refKg) && refKg > 0
      ? refKg
      : Number.isFinite(qtdAtual) && qtdAtual > 0
        ? qtdAtual
        : NaN
  if (!Number.isFinite(baseKg) || baseKg <= 0) return draft

  const opcoes = opcoesArredondamentoEmbalagem(baseKg, produto)
  if (!opcoes) return draft

  const dirExplicita =
    direcao ??
    (draft.embalagem_direcao === 'menos' || draft.embalagem_direcao === 'mais'
      ? draft.embalagem_direcao
      : null)

  if (!dirExplicita) {
    if (!kgTemFracaoEmbalagem(qtdAtual, produto)) {
      return draft
    }
  }

  if (!opcoes.precisaEscolha && !dirExplicita) {
    return { ...draft, embalagem_direcao: '' }
  }

  const dir: DirecaoArredondamentoEmbalagem = dirExplicita ?? 'mais'
  const qtdArred = dir === 'menos' ? opcoes.menos.kg : opcoes.mais.kg
  const next: PedidoItemDraft = {
    ...draft,
    quantidade: String(qtdArred),
    embalagem_direcao: opcoes.precisaEscolha ? dir : '',
  }
  if (quantidadeDiverge(next)) {
    if (!next.justificativa.trim() || isJustificativaArredondamentoEmbalagem(next.justificativa)) {
      next.justificativa = justificativaArredondamentoEmbalagem(dir)
    }
  } else if (isJustificativaArredondamentoEmbalagem(next.justificativa)) {
    next.justificativa = ''
  }
  return next
}

export function normalizeNecessidadeRows(rows: unknown[]): NecessidadePedidoRow[] {
  return rows.map((raw) => {
    const row = raw as Record<string, unknown>
    const cardapio = Number(row.quantidade_cardapio ?? row.quantidade_pnae ?? 0)
    const pnae = Number(row.quantidade_pnae ?? row.quantidade_cardapio ?? 0)
    return {
      alimento_id: String(row.alimento_id),
      alimento: String(row.alimento ?? ''),
      grupo: String(row.grupo ?? ''),
      unidade: String(row.unidade || 'kg'),
      quantidade_pnae: pnae,
      quantidade_cardapio: cardapio,
    }
  })
}

export function formatSemanasList(semanas: number[]): string {
  const uniq = [...new Set(semanas.filter((n) => Number.isFinite(n)))].sort((a, b) => a - b)
  if (!uniq.length) return ''
  if (uniq.length === 1) return String(uniq[0])
  if (uniq.length === 2) return `${uniq[0]} e ${uniq[1]}`
  return `${uniq.slice(0, -1).join(', ')} e ${uniq[uniq.length - 1]}`
}

/** Mensagem pedindo cadastro de ficha/cardápio para os slots do calendário sem itens. */
export function mensagemCardapioPendente(
  pendencias: { trimestre: number; semana: number }[],
): string {
  if (!pendencias.length) {
    return 'Não há necessidade gerada. Aplique a ficha técnica aos dias letivos (Ficha / Pacote) e confira se há itens na ficha.'
  }

  const porTrimestre = new Map<number, number[]>()
  for (const p of pendencias) {
    const t = Number(p.trimestre)
    const s = Number(p.semana)
    if (!Number.isFinite(t) || !Number.isFinite(s)) continue
    const list = porTrimestre.get(t) || []
    list.push(s)
    porTrimestre.set(t, list)
  }

  const partes = [...porTrimestre.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([t, semanas]) => {
      const label = formatSemanasList(semanas)
      return semanas.length === 1
        ? `trimestre ${t}, semana ${label}`
        : `trimestre ${t}, semanas ${label}`
    })

  if (partes.length === 1) {
    return `Ficha/cardápio pendente: aplique a ficha técnica ao ${partes[0]}.`
  }
  return `Ficha/cardápio pendente: aplique a ficha técnica a: ${partes.join('; ')}.`
}

export function emptyPedidoItemRow(): PedidoItemDraft {
  return {
    key: crypto.randomUUID(),
    alimento_id: '',
    alimento_nome: '',
    alimento_grupo: '',
    unidade: 'kg',
    quantidade_pnae: '',
    quantidade: '',
    valor_unitario: '',
    justificativa: '',
    embalagem_direcao: '',
  }
}

export function itensFromCompraDb(
  itens: {
    id: string
    alimento_id: string
    quantidade: number
    quantidade_pnae: number | null
    valor_unitario: number
    justificativa: string | null
    alimento?: { nome: string; categoria?: string }
  }[],
): PedidoItemDraft[] {
  return itens.map((i) => ({
    key: i.id,
    alimento_id: i.alimento_id,
    alimento_nome: i.alimento?.nome ?? '',
    alimento_grupo: i.alimento?.categoria ?? '',
    unidade: 'kg',
    quantidade_pnae: String(i.quantidade_pnae ?? i.quantidade),
    quantidade: String(i.quantidade),
    valor_unitario: String(i.valor_unitario),
    justificativa: i.justificativa ?? '',
    embalagem_direcao: '',
  }))
}

export function quantidadeDiverge(item: PedidoItemDraft): boolean {
  const pnae = Number(item.quantidade_pnae)
  const pedida = Number(item.quantidade)
  if (!Number.isFinite(pnae) || !Number.isFinite(pedida)) return false
  return Math.abs(pnae - pedida) > 0.0005
}

export function previstoPnae(item: {
  quantidade_pnae?: number | string | null
  quantidade?: number | string | null
}): number {
  const p = Number(item.quantidade_pnae)
  if (Number.isFinite(p) && p > 0) return p
  return Number(item.quantidade) || 0
}
